import React from 'react';

import {
  ErrorBoundary,
  SearchProvider,
  Results as AppSearchResults,
  WithSearch,
} from '@elastic/react-search-ui';
import { ConfigHelper } from '../../config/ConfigHelper';
import HeApiConnector from '../../connectors/HeApiConnector';
import { ConnectorProvider } from '../../connectors/ConnectorContext';
import DocumentMetaUpdater from '../../components/DocumentMetaUpdater';
import configJson from './site-search-config.json';
import ConditionalPagination from '../../components/ConditionalPagination';
import SiteSearchResultView from './SiteSearchResultView';
import Layout from '../../components/Layout';
import HelpFooter from '../../components/HelpFooter';
import SiteSearchFilters from './SiteSearchFilters';
import Results from '../../components/Results';
import ResultHeader from '../../components/ResultHeader';
import NoResults from '../../components/NoResults';
import Header from '../../components/Header';
import FiltersButton from '../../components/FiltersButton';
import ErrorBoundaryContent from '../../components/ErrorBoundaryContent';

const configHelper = new ConfigHelper(configJson);

const connector = new HeApiConnector(configHelper.getEndpoints('SITE'));

const config = {
  alwaysSearchOnInitialLoad: true,
  apiConnector: connector,
  initialState: {
    resultsPerPage: configHelper.getPageSizeOptions()[0],
  },
  searchQuery: {
    facets: configHelper.buildFacetConfigFromConfig(),
    ...configHelper.buildSearchOptionsFromConfig(),
    disjunctiveFacets: configHelper.buildDisjunctiveFacetsFromConfig(),
  },
};

export default function SiteSearch() {
  return (
    <SearchProvider config={config}>
      <ConnectorProvider connector={connector}>
        <DocumentMetaUpdater searchName="Site Search" />
        <WithSearch
          mapContextToProps={({
            clearFilters,
            facets,
            filters,
            isLoading,
            wasSearched,
            totalResults,
            resultSearchTerm,
          }) => ({
            clearFilters,
            facets,
            filters,
            isLoading,
            wasSearched,
            totalResults,
            resultSearchTerm,
          })}
        >
          {({
            clearFilters,
            facets,
            filters,
            isLoading,
            wasSearched,
            totalResults,
          }) => {
            return (
              <div className="search-ui" id="main-content">
                <ErrorBoundary view={ErrorBoundaryContent}>
                  <Layout
                    BodyContent={
                      <AppSearchResults
                        resultView={SiteSearchResultView}
                        shouldTrackClickThrough={true}
                        thumbnailField={configHelper.getThumbnailField()}
                        titleField={configHelper.getTitleField()}
                        urlField={configHelper.getUrlField()}
                        view={Results}
                      />
                    }
                    BodyFooter={
                      <>
                        <ConditionalPagination />
                        <HelpFooter />
                      </>
                    }
                    BodyHeader={
                      <ResultHeader
                        configHelper={configHelper}
                        connector={connector}
                        filters={filters}
                        hideDownload={true}
                        hideViewButtons={true}
                        totalResults={totalResults}
                        wasSearched={wasSearched}
                      />
                    }
                    Filters={<SiteSearchFilters />}
                    Header={
                      <Header
                        FiltersButton={
                          <FiltersButton
                            SearchFilters={<SiteSearchFilters />}
                            clearFilters={clearFilters}
                            facets={facets}
                          />
                        }
                      />
                    }
                    NoResults={<NoResults />}
                    filters={filters}
                    isLoading={isLoading}
                    searchType="site"
                    totalResults={totalResults}
                    wasSearched={wasSearched}
                  />
                </ErrorBoundary>
              </div>
            );
          }}
        </WithSearch>
      </ConnectorProvider>
    </SearchProvider>
  );
}
